<template lang="pug">
.button-pagination.font-medium(v-if='status && isDisplayPagination')
  button-base.button-pagination__btn(@click='clickPrevPage()', :data-limited='isSelected(MIN_PAGE)')
    .button-pagination__btn__icon {{ prevButtonCharacter }}

  template(v-if='isDisplayMinPageButton')
    button-base.button-pagination__btn(@click='clickPageNumber(MIN_PAGE)', :data-selected='isSelected(MIN_PAGE)') {{ MIN_PAGE }}
    .button-pagination__ellipsis(v-if='isDisplayMinPageEllipsis') {{ ellipsisButtonCharacter }}

  template(v-for='index of maxPage')
    button-base.button-pagination__btn(
      v-if='isDisplayPageButtonRange(index)',
      @click='clickPageNumber(index)',
      :data-selected='isSelected(index)'
    ) {{ index }}

  template(v-if='isDisplayMaxPageButton')
    .button-pagination__ellipsis(v-if='isDisplayMaxPageEllipsis') {{ ellipsisButtonCharacter }}
    button-base.button-pagination__btn(@click='clickPageNumber(maxPage)', :data-selected='isSelected(maxPage)') {{ maxPage }}

  button-base.button-pagination__btn(@click='clickNextPage()', :data-limited='isSelected(maxPage)')
    .button-pagination__btn__icon {{ nextButtonCharacter }}
</template>

<script lang="ts">
import { Component, Vue, Prop } from 'vue-property-decorator'
import ButtonBase from '@/components/atoms/ButtonBase.vue'
import { PaginationStatus } from '@/models/paginationModel'
import { CommonCharacterSetting } from '@/const/DisplaySettings'

/**
 * ページネーションボタン
 */
@Component({
  components: {
    ButtonBase,
  },
})
export default class ButtonPagination extends Vue {
  /** ページネーション状態情報 */
  @Prop()
  status!: PaginationStatus

  /** 1ページしか存在しない場合の非表示判定 */
  @Prop({ default: true })
  isSinglePageHidden!: boolean

  /** 現在ページを中心に表示するボタン数（奇数設定） */
  @Prop({ default: 3 })
  displayPageRange!: number

  /** 1ページ戻るボタン */
  private prevButtonCharacter = CommonCharacterSetting.PREV_BUTTON

  /** 1ページ進むボタン */
  private nextButtonCharacter = CommonCharacterSetting.NEXT_BUTTON

  /** 省略文字 */
  private ellipsisButtonCharacter = CommonCharacterSetting.ELLIPSIS

  /** 最小ページ */
  private readonly MIN_PAGE = 1

  /** 現在ページ（中心ページ）から両端までのページ数 */
  private get currentPageRangeCount(): number {
    return Math.ceil(this.displayPageRange / 2)
  }

  /** 最大ページ数 */
  private get maxPage(): number {
    const maxPage = Math.ceil(this.status.totalCount / this.status.limit)
    return maxPage < this.MIN_PAGE ? this.MIN_PAGE : maxPage
  }

  /** ページネーションボタン表示判定 */
  private get isDisplayPagination(): boolean {
    return this.isSinglePageHidden ? this.maxPage > this.MIN_PAGE : true
  }

  /** 最小ページボタン表示判定 */
  private get isDisplayMinPageButton(): boolean {
    return this.status.page > this.currentPageRangeCount
  }

  /** 最小ページ側省略記号表示判定 */
  private get isDisplayMinPageEllipsis(): boolean {
    return this.status.page > this.currentPageRangeCount + 1
  }

  /** 最大ページボタン表示判定 */
  private get isDisplayMaxPageButton(): boolean {
    return this.status.page <= this.maxPage - this.currentPageRangeCount
  }

  /** 最大ページ側省略記号表示判定 */
  private get isDisplayMaxPageEllipsis(): boolean {
    return this.status.page <= this.maxPage - this.currentPageRangeCount - 1
  }

  /** 表示ボタン範囲判定 */
  private isDisplayPageButtonRange(pageIndex: number): boolean {
    return (
      pageIndex > this.status.page - this.currentPageRangeCount &&
      pageIndex < this.status.page + this.currentPageRangeCount
    )
  }

  /** 選択ページボタンへのデータ属性付与 */
  private isSelected(selectedPage: number): string {
    return this.status.page === selectedPage ? 'on' : ''
  }

  /** ページボタンクリック処理 */
  private clickPageNumber(selectedPageNumber: number): void {
    this.$emit('click', selectedPageNumber)
  }

  /** ページ後退ボタンクリック処理 */
  private clickPrevPage(): void {
    if (this.status.page > 1) {
      const prevPageNumber = this.status.page - 1
      this.$emit('click', prevPageNumber)
    }
  }

  /** ページ進行ボタンクリック処理 */
  private clickNextPage(): void {
    if (this.status.page < this.maxPage) {
      const nextPageNumber = this.status.page + 1
      this.$emit('click', nextPageNumber)
    }
  }
}
</script>

<style lang="stylus" scoped>
.button-pagination
  display flex
  height 24px
  gap 12px

  &__btn
    display flex
    align-items center
    justify-content center
    width 26px
    height 24px
    border 1px solid var(--color-manager-pagination__border)
    border-radius 4px
    background-color var(--color-manager-pagination__background)
    font-size 12px
    color var(--color-manager-pagination__text)

    &[data-selected='on']
      pointer-events none
      border none
      background-color var(--color-manager-pagination__active-background)
      color var(--color-manager-pagination__active-text)

    &[data-limited='on']
      pointer-events none
      cursor auto

    &__icon
      height 12px

  &__ellipsis
    display flex
    align-items center
    justify-content center
    width 10px
    height 24px
    font-size 12px
    color var(--color-manager-pagination__text)
</style>
